import { useState } from "react"
import type { CombinedError } from "urql"
import { useQuery } from "urql"

import { APPROVED_PRODUCTS_PAGE_SIZE } from "../constants"
import { deduplicateApprovedProducts, escapeIlikeWildcards } from "../utils"

import {
  ApprovalStatus,
  type CompanyProductAssociationLocationQueryableFields,
  ComputedProductStatus,
  GetCompanyProductAssociationListDocument,
  type GetCompanyProductAssociationListQuery,
  type GetCompanyProductAssociationListQueryVariables,
} from "@/graphql/codegen/graphql"
import type { CompanyProductAssociationListItem } from "@/screens/CustomerProducts/stores/useCustomerProductAssociationsStore"

interface UseGetApprovedProductsParams {
  companyId: string
  shipToLocationId: string
  query: string
}

interface UseGetApprovedProductsReturn {
  approvedProducts: CompanyProductAssociationListItem[]
  isFetching: boolean
  error: CombinedError | undefined
  loadMore: () => void
  refetch: () => void
}

// Everything accumulated across pages for one queryKey — kept in a single object so a query-key
// change or a newly-committed page resets/updates all of it in one setState instead of several.
interface ApprovedProductsPageState {
  queryKey: string
  offset: number
  approvedProducts: CompanyProductAssociationListItem[]
  lastCommittedData: GetCompanyProductAssociationListQuery | undefined
  total: number | undefined
  rawFetchedCount: number
  lastPageItemCount: number | undefined
}

const buildInitialPageState = (queryKey: string): ApprovedProductsPageState => ({
  queryKey,
  offset: 0,
  approvedProducts: [],
  lastCommittedData: undefined,
  total: undefined,
  rawFetchedCount: 0,
  lastPageItemCount: undefined,
})

const buildApprovedProductsQueryKey = ({ companyId, shipToLocationId, query }: UseGetApprovedProductsParams): string =>
  `${companyId}::${shipToLocationId}::${query}`

export const useGetApprovedProducts = ({
  companyId,
  shipToLocationId,
  query,
}: UseGetApprovedProductsParams): UseGetApprovedProductsReturn => {
  const queryKey = buildApprovedProductsQueryKey({ companyId, shipToLocationId, query })

  const [pageState, setPageState] = useState<ApprovedProductsPageState>(() => buildInitialPageState(queryKey))

  if (pageState.queryKey !== queryKey) {
    setPageState(buildInitialPageState(queryKey))
  }

  const filter: CompanyProductAssociationLocationQueryableFields = {
    shipToLocationIds: [shipToLocationId],
    approvalStatus: [ApprovalStatus.Approved],
    computedProductStatuses: [ComputedProductStatus.Active],
    ...(query && { query: escapeIlikeWildcards(query) }),
  }

  const variables: GetCompanyProductAssociationListQueryVariables = {
    input: { companyId, filter, offset: pageState.offset, limit: APPROVED_PRODUCTS_PAGE_SIZE },
  }

  const [{ data, fetching, error, operation }, reexecuteQuery] = useQuery<GetCompanyProductAssociationListQuery>({
    query: GetCompanyProductAssociationListDocument,
    variables,
    pause: !companyId || !shipToLocationId,
  })

  // urql keeps the previous result (and `operation`) visible while a new request for
  // different variables is in flight — comparing operation.variables to what this render
  // actually asked for stops a stale response from committing into a just-reset list.
  const isDataForCurrentQuery =
    data !== undefined && operation !== undefined && JSON.stringify(operation.variables) === JSON.stringify(variables)

  if (isDataForCurrentQuery && data !== pageState.lastCommittedData) {
    const newItems = data.companyProductAssociation.list.items
    const isFirstPage = pageState.offset === 0

    setPageState((prev) => ({
      ...prev,
      lastCommittedData: data,
      total: data.companyProductAssociation.list.pageInfo.total,
      lastPageItemCount: newItems.length,
      rawFetchedCount: isFirstPage ? newItems.length : prev.rawFetchedCount + newItems.length,
      approvedProducts: deduplicateApprovedProducts(isFirstPage ? newItems : [...prev.approvedProducts, ...newItems]),
    }))
  }

  // hasMore is derived from raw fetched counts, not the deduplicated `approvedProducts.length` —
  // dedup can drop a row without the server-reported total shrinking to match, which would
  // otherwise make hasMore permanently true and loadMore never terminate.
  const hasMoreByTotal = pageState.total === undefined || pageState.rawFetchedCount < pageState.total
  const hasFullLastPage =
    pageState.lastPageItemCount === undefined || pageState.lastPageItemCount === APPROVED_PRODUCTS_PAGE_SIZE
  const hasMore = hasMoreByTotal && hasFullLastPage

  const loadMore = () => {
    if (!hasMore || fetching) return
    setPageState((prev) => ({ ...prev, offset: prev.offset + APPROVED_PRODUCTS_PAGE_SIZE }))
  }

  // Resetting pageState clears the accumulated pages and lastCommittedData so the fresh
  // response commits; network-only forces a round trip even when offset was already 0 and
  // the variables are therefore otherwise unchanged.
  const refetch = () => {
    setPageState(buildInitialPageState(queryKey))
    reexecuteQuery({ requestPolicy: "network-only" })
  }

  return { approvedProducts: pageState.approvedProducts, isFetching: fetching, error, loadMore, refetch }
}
